import React, { useState, useContext, useEffect, useRef } from "react";
import { ThemeContext } from "./App";
import { useNavigate } from "react-router-dom";
import { getChatbotAnswer, sendMessageToAdmin } from "./components/chatbotApi";
import io from "socket.io-client";

const SOCKET_URL = "https://tradespots.online";

const ContactUs = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]); // For live chat messages
  const [showChatbox, setShowChatbox] = useState(false);
  const [chatbotModalOpen, setChatbotModalOpen] = useState(false);
  const [chatbotMessages, setChatbotMessages] = useState([
    { from: "bot", text: "Hi! How can I help you today?" },
  ]);
  const [chatbotInput, setChatbotInput] = useState("");
  const [chatbotTyping, setChatbotTyping] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const chatEndRef = useRef(null);
  const socketRef = useRef();

  // Theme-based colors (match Team.js, Profile.js)
  const isDark = theme === "dark";
  const bgMain = isDark
    ? "linear-gradient(135deg, #181f2a 0%, #23272f 100%)"
    : "linear-gradient(135deg,rgb(115, 177, 247) 0%,rgb(136, 187, 248) 60%,rgb(211, 227, 244) 100%)";
  const colorMain = isDark ? "#f1f1f1" : "#333";
  const cardBg = isDark ? "#23272f" : "#fff";
  const cardShadow = isDark ? "0 8px 20px rgba(255,255,255,0.08)" : "0 8px 15px rgba(0,0,0,0.1)";
  const borderColor = isDark ? "#333" : "#ccc";
  const btnBg = isDark ? "#1e88e5" : "#007bff";
  const btnHover = isDark ? "#1565c0" : "#16325c";

  // Scroll to bottom on new message
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch all messages between user and admin
  const fetchMessages = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    try {
      const res = await fetch(`https://tradespots.online/api/contact-messages/user/${userId}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.messages)) {
        setMessages(
          data.messages.map(msg => ({
            text: msg.message,
            sender: msg.from === "admin" ? "admin" : "user",
            time: msg.createdAt ? new Date(msg.createdAt) : new Date()
          }))
        );
      } else {
        setMessages([]);
      }
    } catch {
      setMessages([]);
    }
  };

  // Fetch message history when chatbox is opened
  useEffect(() => {
    if (showChatbox) {
      fetchMessages();
    }
    // Optionally, you can fetch on mount if chatbox is open
    // eslint-disable-next-line
  }, [showChatbox]);

  // Real-time socket connection
  useEffect(() => {
    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });
    const userId = localStorage.getItem("userId");
    if (userId) {
      socketRef.current.emit("join", { userId });
    }
    socketRef.current.on("newAdminReply", (data) => {
      fetchMessages(); // Always re-fetch full history on new admin reply
    });
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const BOOSTED_SYSTEM_PROMPT = `You are TradeSpot Assistant AI. Your mission is to:
  - Respond with warmth, empathy, and encouragement, as if you are a world-class human mentor.
  - Use natural, conversational language, with a touch of personality and humor when appropriate.
  - Give step-by-step, crystal-clear, and actionable guidance, referencing the UI and code as needed.
  - Proactively anticipate user needs, clarify ambiguities, and offer extra tips or best practices.
  - If the user is confused, reassure them and break down complex ideas into simple, relatable explanations.
  - If you don't know, admit it honestly and suggest the best next step or where to get help.
  - Always sound positive, supportive, and never robotic or generic.
  - Use analogies, examples, and stories to make answers memorable and engaging.
  - If the user is frustrated, acknowledge their feelings and offer encouragement.
  - End answers with a friendly closing, invitation to ask more, or a motivational note.

  You have access to the full, up-to-date code and documentation for the TradeSpot platform. Use it to ensure every answer is accurate, detailed, and user-focused. Your goal is to make every user feel understood, empowered, and delighted.`;

  function formatChatHistory(messages) {
    return messages.slice(-15).map((m) => ({
      role: m.from === "user" ? "user" : "assistant",
      content: m.text,
    }));
  }

  const handleChatbotSend = async () => {
    if (!chatbotInput.trim()) return;
    const userMsg = { from: "user", text: chatbotInput };
    setChatbotMessages((msgs) => [...msgs, userMsg]);
    setChatbotInput("");
    setChatbotTyping(true);
    try {
      const userId = localStorage.getItem("userId") || "anonymous";
      const systemPrompt = BOOSTED_SYSTEM_PROMPT;
      const answer = await getChatbotAnswer({
        question: chatbotInput,
        userId,
        systemPrompt,
        chatHistory: formatChatHistory([...chatbotMessages, userMsg]),
      });
      setChatbotMessages((msgs) => [
        ...msgs,
        { from: "bot", text: answer || "Sorry, I don't know the answer to that." },
      ]);
      setChatbotTyping(false);
    } catch (e) {
      setChatbotMessages((msgs) => [
        ...msgs,
        { from: "bot", text: "Sorry, I couldn't get an answer right now." },
      ]);
      setChatbotTyping(false);
    }
  };

  const handleChatbotEscalate = async () => {
    setChatbotMessages((msgs) => [...msgs, { from: "bot", text: "Your message has been sent to an admin. Please wait for a response." }]);
    const lastUserMsg = chatbotMessages.filter((m) => m.from === "user").slice(-1)[0];
    if (!lastUserMsg) return;
    try {
      const sender = localStorage.getItem("userId") || "anonymous";
      await sendMessageToAdmin({ text: lastUserMsg.text, sender });
    } catch (e) {
      setChatbotMessages((msgs) => [...msgs, { from: "bot", text: "Failed to send message to admin." }]);
    }
  };

  // Send user message to backend and emit via socket
  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    const userId = localStorage.getItem("userId"); // must be MongoDB _id
    const username = localStorage.getItem("username") || "User";
    if (socketRef.current && userId) {
      socketRef.current.emit("userMessage", { userId, username, message });
    }
    setMessage("");
    try {
      await fetch("https://tradespots.online/api/contact-messages/user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, username, message }),
      });
      fetchMessages(); // Always re-fetch after sending
    } catch (err) {
      // Optionally show error to user
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: bgMain,
        color: colorMain,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
    >
      {/* Contact Us Header */}
      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          marginTop: 24,
          marginBottom: 8,
          color: isDark ? "#f1f1f1" : "#222", // <-- Fix: use light color in dark mode
          textAlign: "center",
          fontFamily: '"Anton", "Oswald", "Impact", "Arial Black", "Concrete", Arial, sans-serif',
          letterSpacing: 2,
          textTransform: "uppercase",
          lineHeight: 1.05,
          borderRadius: 6,
          padding: "2px 18px",
          display: "inline-block",
          boxShadow: isDark ? "0 2px 8px #111" : "0 2px 8px #b3c6e0",
          border: isDark ? "2px solid #23272f" : "2px solid #b3c6e0",
          textShadow: isDark ? "0 2px 8px #111" : "0 2px 8px #b3c6e0",
        }}
      >
        TradeSpot Support
      </div>
      <div
        style={{
          display: showChatbox ? "none" : "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 24,
          marginTop: 40,
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", gap: 48 }}>
          {/* Email Icon */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => {
                window.location.href = "mailto:support@tradespot.online";
              }}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "3px solid #1e88e5",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(30,136,229,0.15)",
                cursor: "pointer",
                transition: "box-shadow 0.2s, border 0.2s",
                outline: "none",
              }}
              aria-label="Send email to support@tradespot.online"
            >
              <img
                src="https://ssl.gstatic.com/ui/v1/icons/mail/rfr/gmail.ico"
                alt="Email"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #1e88e5)" }}
              />
            </button>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              Email
            </span>
          </div>
          {/* Telegram Icon */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <a
              href="https://t.me/tradespothelp"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "3px solid #229ED9",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(34,158,217,0.15)",
                transition: "box-shadow 0.2s, border 0.2s",
              }}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
                alt="Telegram"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #229ED9)" }}
              />
            </a>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              Telegram
            </span>
          </div>
          {/* TS Icon (now opens chatbox) */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => setShowChatbox(true)}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#23272f",
                border: "3px solid #bfc3c9",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(100,100,100,0.15)",
                cursor: "pointer",
                transition: "box-shadow 0.2s, border 0.2s",
                outline: "none",
              }}
              aria-label="Open TradeSpot Chatbox"
            >
              <img
                src={require("./tradespot-login.jpg")}
                alt="TS"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #bfc3c9)" }}
              />
            </button>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              Support
            </span>
          </div>
        </div>
        {/* Row below: Phone and Bot icons */}
        <div style={{ display: "flex", gap: 48, marginTop: 16 }}>
          {/* Phone Icon */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => setShowCallModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "3px solid #43a047",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(67,160,71,0.15)",
                transition: "box-shadow 0.2s, border 0.2s",
                textDecoration: "none",
                cursor: "pointer",
                outline: "none"
              }}
              aria-label="Show call modal for +1 681 249 8988"
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/724/724664.png"
                alt="Phone"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #43a047)" }}
              />
            </button>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              Phone
            </span>
          </div>
          {/* Bot Icon */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button
              onClick={() => setChatbotModalOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "3px solid #ffb300",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(255,179,0,0.15)",
                cursor: "pointer",
                transition: "box-shadow 0.2s, border 0.2s",
                outline: "none",
              }}
              aria-label="Open TradeSpot Chatbot"
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/4712/4712027.png"
                alt="Bot"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #ffb300)" }}
              />
            </button>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              AI Bot
            </span>
          </div>
          {/* WhatsApp Icon */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "#fff",
                border: "3px solid #25D366",
                borderRadius: 16,
                padding: 8,
                boxShadow: "0 4px 16px rgba(37,211,102,0.15)",
                transition: "box-shadow 0.2s, border 0.2s",
              }}
              aria-label="WhatsApp"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                alt="WhatsApp"
                style={{ width: 48, height: 48, borderRadius: 12, filter: "drop-shadow(0 2px 8px #25D366)" }}
              />
            </div>
            <span
              style={{
                marginTop: 8,
                fontSize: 16,
                color: colorMain,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              WhatsApp
            </span>
          </div>
        </div>
      </div>
      {/* Back Button */}
      <div
        style={{
          position: "absolute",
          left: 20,
          top: 20,
          zIndex: 20,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: btnBg,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "6px 16px",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            transition: "background 0.2s",
          }}
          onMouseDown={(e) => (e.currentTarget.style.background = btnHover)}
          onMouseUp={(e) => (e.currentTarget.style.background = btnBg)}
          onMouseLeave={(e) => (e.currentTarget.style.background = btnBg)}
        >
          ← Back
        </button>
      </div>
      {/* Chatbox overlay */}
      {showChatbox && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            id="tradespot-chatbox"
            style={{
              width: "100%",
              maxWidth: 370,
              height: 540, // Fixed height for modal
              background: cardBg,
              boxShadow: cardShadow,
              borderRadius: 16,
              border: `1px solid ${borderColor}`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowChatbox(false)}
              style={{
                position: "absolute",
                right: 12,
                top: 12,
                background: btnBg,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "6px 16px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                zIndex: 10,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "background 0.2s",
              }}
              onMouseDown={(e) => (e.currentTarget.style.background = btnHover)}
              onMouseUp={(e) => (e.currentTarget.style.background = btnBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = btnBg)}
            >
              Close
            </button>
            <div
              style={{
                padding: "18px 24px",
                borderBottom: `1px solid ${borderColor}`,
                fontWeight: 700,
                fontSize: 22,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              TradeSpot Support
            </div>
            <div
              style={{
                flex: 1,
                padding: 16,
                overflowY: "auto",
                background: isDark ? "#181c23" : "#f7fafd",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                minHeight: 0, // allow flexbox to work
                maxHeight: 350, // fixed scrollable area for messages
              }}
            >
              {messages.length === 0 && (
                <div style={{ color: "#888", textAlign: "center", marginTop: 40 }}>
                  No messages yet. Start the conversation!
                </div>
              )}
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                    background: msg.sender === "user" ? btnBg : isDark ? "#23272f" : "#e3eafc",
                    color: msg.sender === "user" ? "#fff" : colorMain,
                    borderRadius: 12,
                    padding: "8px 14px",
                    marginBottom: 2,
                    maxWidth: "80%",
                    fontSize: 15,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  {msg.text}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <form
              onSubmit={handleSend}
              style={{
                display: "flex",
                alignItems: "center",
                borderTop: `1px solid ${borderColor}`,
                background: isDark ? "#181c23" : "#f7fafd",
                padding: 12,
                gap: 8,
              }}
            >
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${borderColor}`,
                  background: isDark ? "#23272f" : "#fff",
                  color: colorMain,
                  fontSize: 15,
                  outline: "none",
                }}
                autoFocus
              />
              <button
                type="submit"
                style={{
                  background: btnBg,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 18px",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseDown={(e) => (e.currentTarget.style.background = btnHover)}
                onMouseUp={(e) => (e.currentTarget.style.background = btnBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = btnBg)}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Chatbot overlay */}
      {chatbotModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            background: "rgba(0,0,0,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 340,
              height: 480,
              maxWidth: "95vw",
              maxHeight: "95vh",
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              animation: "fadeIn 0.2s",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                background: "#007bff",
                color: "#fff",
                padding: "10px 16px",
                fontWeight: 600,
                fontSize: 17,
                borderRadius: "18px 18px 0 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              TradeSpot AI
              <button
                onClick={() => setChatbotModalOpen(false)}
                style={{
                  background: "#fff",
                  border: "none",
                  color: "#007bff",
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 6,
                  padding: "4px 14px",
                  cursor: "pointer",
                  marginLeft: 8
                }}
              >
                Close
              </button>
            </div>
            <div
              style={{
                flex: 1,
                padding: 16,
                overflowY: "auto",
                background: "#f7faff",
              }}
            >
              {chatbotMessages.map((msg, i) => (
                <div key={i} style={{ textAlign: msg.from === "user" ? "right" : "left", margin: "8px 0" }}>
                  <span
                    style={{
                      display: "inline-block",
                      background: msg.from === "user" ? "#e6f7ff" : "#e9ecef",
                      color: "#222",
                      borderRadius: 8,
                      padding: "8px 14px",
                      maxWidth: 220,
                      wordBreak: "break-word",
                      fontSize: 15,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                    }}
                  >
                    {msg.text}
                  </span>
                  {msg.text.includes("connect you to an admin") ? (
                    <button
                      onClick={handleChatbotEscalate}
                      style={{
                        marginLeft: 8,
                        fontSize: 13,
                        background: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: 4,
                        padding: "2px 10px",
                        cursor: "pointer",
                      }}
                    >
                      Yes
                    </button>
                  ) : null}
                </div>
              ))}
              {chatbotTyping && (
                <div style={{ textAlign: "left", margin: "8px 0", color: "#888", fontStyle: "italic" }}>
                  TradeSpot is typing...
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                padding: 12,
                borderTop: "1px solid #e3e3e3",
                background: "#f7faff",
              }}
            >
              <input
                value={chatbotInput}
                onChange={(e) => setChatbotInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleChatbotSend()}
                style={{
                  flex: 1,
                  borderRadius: 6,
                  border: "1px solid #b3d1f7",
                  padding: 8,
                  fontSize: 15,
                }}
                placeholder="Type your question..."
              />
              <button
                onClick={handleChatbotSend}
                style={{
                  marginLeft: 8,
                  borderRadius: 6,
                  background: "#007bff",
                  color: "#fff",
                  border: "none",
                  padding: "8px 18px",
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Call confirmation modal */}
      {showCallModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.45)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: cardBg,
              borderRadius: 14,
              boxShadow: cardShadow,
              padding: 32,
              minWidth: 320,
              maxWidth: "90vw",
              textAlign: "center",
              border: `1px solid ${borderColor}`,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Call TradeSpot Support?</div>
            <div style={{ fontSize: 16, marginBottom: 18, color: colorMain }}>
              <span style={{ fontWeight: 600 }}>+1 681 249 8988</span>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 18 }}>
              <button
                onClick={() => {
                  setShowCallModal(false);
                  window.location.href = "tel:+16812498988";
                }}
                style={{
                  background: btnBg,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 22px",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                  marginRight: 8,
                  transition: "background 0.2s",
                }}
                onMouseDown={e => (e.currentTarget.style.background = btnHover)}
                onMouseUp={e => (e.currentTarget.style.background = btnBg)}
                onMouseLeave={e => (e.currentTarget.style.background = btnBg)}
              >
                Yes
              </button>
              <button
                onClick={() => setShowCallModal(false)}
                style={{
                  background: isDark ? "#23272f" : "#eee",
                  color: colorMain,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 8,
                  padding: "8px 22px",
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: "pointer",
                  marginLeft: 8,
                  transition: "background 0.2s",
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
